import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import logoBlack from "../assets/logo-black.png";
import logoWhite from "../assets/logo-white.png";

interface NavbarProps {
  navbar: { label: string; section: string }[];
  activeSection: string;
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
  scrollToSection: (id: string) => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  navbar,
  activeSection,
  mobileMenuOpen,
  setMobileMenuOpen,
  scrollToSection,
}) => {
  const [scrollY, setScrollY] = useState(0);
  const [lastY, setLastY] = useState(0);
  const [navbarShow, setNavbarShow] = useState(true);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrollY(y);
      setNavbarShow(!(y > 300 && y - lastY > 0));
      setLastY(y);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [lastY]);

  const isScrolled = scrollY > 120;
  const navTextMuted = isScrolled ? "text-[var(--text-secondary)] hover:text-[var(--text-primary)]" : "text-white/85 hover:text-white";
  const navBg = isScrolled ? "bg-[#F1F4F3c9] border-black/10 shadow" : "bg-transparent border-transparent";

  return (
    <nav className={`fixed w-full z-50 backdrop-blur-sm duration-300 ${navbarShow ? "top-0" : "-top-24"} ${navBg}`}>
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 h-16">
        <div
          className="flex items-center cursor-pointer select-none"
          onClick={() => scrollToSection("home")}
        >
          <img
            src={isScrolled ? logoBlack : logoWhite}
            alt="Kapsül Tarım Ideathonu"
            className="h-8 sm:h-9 w-auto object-contain translate-y-2"
          />
        </div>

        <div className="hidden lg:flex items-center space-x-6">
          {navbar.map(({ label, section }, idx) => (
            <button
              key={idx}
              onClick={() => scrollToSection(section)}
              className={`group relative cursor-pointer text-sm font-medium transition-colors select-none bg-transparent border-none pb-1 ${navTextMuted}`}
            >
              {label}
              <span
                className={`absolute -bottom-1 left-0 h-0.5 bg-[#73E03A] transition-all duration-300 ${
                  activeSection === section ? "w-full" : "w-0 group-hover:w-full"
                }`}
              />
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            className={`lg:hidden kapsul-focus h-10 w-10 inline-flex items-center justify-center bg-transparent border-none ${
              isScrolled ? "text-[var(--text-primary)]" : "text-white"
            }`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Menüyü aç veya kapat"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden border-t overflow-hidden bg-[#F4FAF0f2] border-[#2f6f1e]/20"
          >
            <div className="px-4 py-3 space-y-2">
              {navbar.map(({ label, section }, idx) => (
                <motion.button
                  initial={{ x: -10, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.25, delay: idx * 0.05 }}
                  key={idx}
                  onClick={() => scrollToSection(section)}
                  className="block w-full text-left py-2 text-base cursor-pointer select-none bg-transparent border-none text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                >
                  {label}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
